import {CSSProperties, MouseEvent, useLayoutEffect, useRef, useState} from 'react';
import { Pile, PileDragState, PileDataProperties } from "./Pile";
import './Board.css';
import { DraggingPile } from './DraggingPile';

export interface PileDisplayProperties extends PileDataProperties {
    row: number;
    col: number;
}

export interface BoardProperties {
    rows: number;
    cols: number;
    piles: PileDisplayProperties[];
}

interface PileDragPos {
    top: number;
    left: number;
}

export function Board({rows, cols, piles}: BoardProperties) {
    const ref = useRef<HTMLDivElement>(null);

    const [width, setWidth] = useState([0, 0]);
    const [pileDragState, setPileDragState] = useState<PileDragState>();
    const [pileDragPos, setPileDragPos] = useState<PileDragPos>({top: 0, left: 0});
    const [pileHoverId, setPileHoverId] = useState<string>();

    function autoResize() {
        const widthTot = ref.current?.parentElement?.offsetWidth ?? 0;
        const widthLoc = Math.min(widthTot, 1024);
        const pad = (widthTot - widthLoc) / 2;
        setWidth([widthLoc, pad]);
    }

    useLayoutEffect(() => {
        window.addEventListener('resize', autoResize)
        autoResize();
    }, [])
    
    const gutter = 0.1*width[0]/cols;
    const cellWidth = width[0]/cols - gutter;
    const cellHeight = 1.45*cellWidth - gutter;

    const boardStyle: CSSProperties = {
        gridTemplateColumns: `repeat(${cols}, ${cellWidth}px)`,
        gridTemplateRows: `repeat(${rows}, ${cellHeight}px)`,
        rowGap: `${gutter}px`,
        columnGap: `${gutter}px`
    }

    function onStartPileDrag(state: PileDragState) {
        if (state.xPos !== undefined && state.yPos !== undefined) {
            setPileDragPos(() => ({
                top: state.yPos as number,
                left: state.xPos as number
            }));
        }

        setPileDragState((oldState) => {
            if (oldState!==undefined) {
                oldState.onRelease();
            }
            return state;
        });

    }

    function getDraggingPileComponent() {
        const rect = ref.current?.getBoundingClientRect();
        return (pileDragState === undefined) ? undefined : (
            <DraggingPile 
                cards={pileDragState.cards} 
                direction={pileDragState.direction}
                width={pileDragState.width} 
                height={pileDragState.height} 
                top={pileDragPos.top - pileDragState.yOff - (rect?.top ?? 0)}
                left={pileDragPos.left - pileDragState.xOff - (rect?.left ?? 0)}
            />
        );
    }

    function dragPile(e: MouseEvent) {
        setPileDragPos(() => ({
            top: e.clientY,
            left: e.clientX
        }));
    }

    function dragPileTouch(xpos: number, ypos: number) {
        setPileDragPos(() => ({
            top: ypos,
            left: xpos
        }));

        let dragOverEl = document.elementFromPoint(xpos, ypos);
        while (dragOverEl) {
            const dragOverPileId = dragOverEl.getAttribute('data-pileid');
            if (dragOverPileId) {
                console.log(`Board: Touch Move ${dragOverPileId}`);
                setPileHoverId(() => dragOverPileId);
                return;
            }
            dragOverEl = dragOverEl.parentElement;
        }
        setPileHoverId(() => undefined);
    }

    function releasePile() {
        if (pileHoverId !== undefined) {
            pileDragState?.onDrop(pileHoverId);
        } else {
            pileDragState?.onRelease();
        }
        setPileDragState(() => undefined);
    }

    function enterPile(pileId: string) {
        setPileHoverId(() => pileId);
    }

    function leavePile(pileId: string) {
        setPileHoverId((oldPileId) => oldPileId === pileId ? undefined : oldPileId);
    }

    return (<div ref={ref} 
        className="solitaire-board" 
        style={boardStyle} 
        onMouseMove={dragPile}
        onMouseUp={releasePile}
        onTouchEnd={releasePile}
        onMouseLeave={releasePile}
    >
         {piles.map((pile, index) => {
            const pileStyle: CSSProperties = {
                gridRow: `${pile.row} / span 1`,
                gridColumn: `${pile.col} / span 1`,
                left: '0px'
            };
            return <div className="pile-container" style={pileStyle} key={pile.pileId} data-pileid={pile.pileId}> 
                <Pile 
                    pileId={pile.pileId}
                    direction={pile.direction}
                    clickable={pile.clickable}
                    onPileDrag={onStartPileDrag}
                    onPileDragMove={dragPileTouch}
                    onPileEnter={enterPile}
                    onPileLeave={leavePile}
                 />
            </div>
        })}
        { getDraggingPileComponent() }
    </div>)
}